import React, {
  useMemo,
  useState
} from "react";

import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  makeStyles
} from "@material-ui/core";

import {
  HelpOutline,
  Launch,
  PlayCircleOutline
} from "@material-ui/icons";

import {
  useHistory,
  useLocation
} from "react-router-dom";

import useHelps
  from "../../hooks/useHelps";

const useStyles =
  makeStyles(theme => ({
    headerButton: {
      color: "white"
    },

    inlineButton: {
      color:
        theme.palette.primary.main
    },

    loading: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 120
    },

    empty: {
      padding:
        theme.spacing(3),
      textAlign: "center",
      borderRadius: 10
    },

    item: {
      padding:
        theme.spacing(2),
      marginBottom:
        theme.spacing(2),
      borderRadius: 10,
      border:
        `1px solid ${theme.palette.divider}`
    },

    itemHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent:
        "space-between",
      gap: 12,
      marginBottom:
        theme.spacing(1)
    },

    title: {
      fontWeight: 700
    },

    description: {
      whiteSpace: "pre-line",
      color:
        theme.palette.text.secondary,
      marginBottom:
        theme.spacing(2)
    },

    actions: {
      display: "flex",
      flexWrap: "wrap",
      gap: 8
    },

    routeText: {
      color:
        theme.palette.text.secondary
    }
  }));

const normalizePath =
  value => {
    let path =
      String(
        value || ""
      )
        .trim()
        .toLowerCase();

    if (!path) {
      return "";
    }

    if (!path.startsWith("/")) {
      path =
        `/${path}`;
    }

    if (
      path.length > 1 &&
      path.endsWith("/")
    ) {
      path =
        path.slice(
          0,
          -1
        );
    }

    return path;
  };

const normalizeContext =
  value =>
    String(
      value || ""
    )
      .trim()
      .toLowerCase();

const routeMatches =
  (
    configuredRoute,
    currentPath
  ) => {
    const configured =
      normalizePath(
        configuredRoute
      );

    const current =
      normalizePath(
        currentPath
      );

    if (
      !configured ||
      !current
    ) {
      return false;
    }

    if (
      configured.endsWith("*")
    ) {
      const prefix =
        configured.slice(
          0,
          -1
        );

      return current.startsWith(
        prefix
      );
    }

    if (
      configured === "/"
    ) {
      return current === "/";
    }

    return (
      current === configured ||
      current.startsWith(
        `${configured}/`
      )
    );
  };

const ContextHelpButton = ({
  contextKey = "",
  mode = "header",
  tooltip = "Ajuda desta tela",
  dialogTitle = "Ajuda desta tela"
}) => {
  const classes =
    useStyles();

  const location =
    useLocation();

  const history =
    useHistory();

  const {
    list
  } =
    useHelps();

  const [
    open,
    setOpen
  ] =
    useState(false);

  const [
    loading,
    setLoading
  ] =
    useState(false);

  const [
    records,
    setRecords
  ] =
    useState([]);

  const currentPath =
    location.pathname;

  const normalizedContextKey =
    normalizeContext(
      contextKey
    );

  const contextualRecords =
    useMemo(() => {
      return records
        .filter(record => {
          if (
            !routeMatches(
              record.route,
              currentPath
            )
          ) {
            return false;
          }

          const recordContext =
            normalizeContext(
              record.contextKey
            );

          /*
           * Sem contextKey:
           * mostra somente ajuda geral
           * da página.
           *
           * Com contextKey:
           * mostra somente ajuda daquele
           * modal, aba ou função.
           */
          if (
            normalizedContextKey
          ) {
            return (
              recordContext ===
              normalizedContextKey
            );
          }

          return !recordContext;
        })
        .sort(
          (a, b) => {
            const orderA =
              Number(
                a.sortOrder || 0
              );

            const orderB =
              Number(
                b.sortOrder || 0
              );

            if (
              orderA !== orderB
            ) {
              return (
                orderA -
                orderB
              );
            }

            return String(
              a.title || ""
            ).localeCompare(
              String(
                b.title || ""
              ),
              "pt-BR"
            );
          }
        );
    }, [
      records,
      currentPath,
      normalizedContextKey
    ]);

  const handleOpen =
    async () => {
      setOpen(true);
      setLoading(true);

      try {
        const data =
          await list();

        setRecords(
          Array.isArray(data)
            ? data
            : []
        );
      } catch (error) {
        setRecords([]);
      } finally {
        setLoading(false);
      }
    };

  const handleCentralHelp =
    () => {
      setOpen(false);

      history.push(
        "/helps"
      );
    };

  const openVideo =
    video => {
      if (!video) {
        return;
      }

      window.open(
        `https://www.youtube.com/watch?v=${video}`,
        "_blank",
        "noopener,noreferrer"
      );
    };

  const openLink =
    link => {
      if (!link) {
        return;
      }

      window.open(
        link,
        "_blank",
        "noopener,noreferrer"
      );
    };

  /*
   * O botão global não aparece
   * na própria Central de Ajuda.
   */
  if (
    !normalizedContextKey &&
    normalizePath(
      currentPath
    ) === "/helps"
  ) {
    return null;
  }

  return (
    <>
      <Tooltip
        title={tooltip}
      >
        <IconButton
          className={
            mode === "inline"
              ? classes.inlineButton
              : classes.headerButton
          }
          onClick={
            handleOpen
          }
          aria-label={
            tooltip
          }
          size="small"
        >
          <HelpOutline />
        </IconButton>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() =>
          setOpen(false)
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {dialogTitle}
        </DialogTitle>

        <DialogContent
          dividers
        >
          {!normalizedContextKey && (
            <>
              <Typography
                variant="body2"
                className={
                  classes.routeText
                }
              >
                Tela atual:{" "}
                <strong>
                  {currentPath}
                </strong>
              </Typography>

              <Divider
                style={{
                  margin:
                    "12px 0 16px"
                }}
              />
            </>
          )}

          {loading ? (
            <div
              className={
                classes.loading
              }
            >
              <CircularProgress
                size={32}
              />
            </div>
          ) : contextualRecords.length >
            0 ? (
            contextualRecords.map(
              record => (
                <Paper
                  key={
                    record.id
                  }
                  elevation={0}
                  className={
                    classes.item
                  }
                >
                  <div
                    className={
                      classes.itemHeader
                    }
                  >
                    <Typography
                      variant="subtitle1"
                      className={
                        classes.title
                      }
                    >
                      {record.title}
                    </Typography>

                    <Chip
                      size="small"
                      label={
                        record.category ||
                        "Geral"
                      }
                    />
                  </div>

                  <Typography
                    variant="body2"
                    className={
                      classes.description
                    }
                  >
                    {record.description ||
                      "Consulte este conteúdo para saber mais sobre esta função."}
                  </Typography>

                  {(record.video ||
                    record.link) && (
                    <div
                      className={
                        classes.actions
                      }
                    >
                      {record.video && (
                        <Button
                          size="small"
                          color="primary"
                          variant="contained"
                          startIcon={
                            <PlayCircleOutline />
                          }
                          onClick={() =>
                            openVideo(
                              record.video
                            )
                          }
                        >
                          Ver vídeo
                        </Button>
                      )}

                      {record.link && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={
                            <Launch />
                          }
                          onClick={() =>
                            openLink(
                              record.link
                            )
                          }
                        >
                          Abrir link
                        </Button>
                      )}
                    </div>
                  )}
                </Paper>
              )
            )
          ) : (
            <Paper
              elevation={0}
              className={
                classes.empty
              }
            >
              <HelpOutline
                style={{
                  fontSize: 44,
                  opacity: 0.45
                }}
              />

              <Typography
                variant="subtitle1"
                gutterBottom
              >
                Ainda não há ajuda
                específica para esta
                função.
              </Typography>

              <Typography
                variant="body2"
                color="textSecondary"
              >
                Consulte a Central de
                Ajuda para ver todos
                os conteúdos
                disponíveis.
              </Typography>
            </Paper>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={
              handleCentralHelp
            }
            color="primary"
          >
            Abrir Central de Ajuda
          </Button>

          <Button
            onClick={() =>
              setOpen(false)
            }
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ContextHelpButton;
